import { useState } from "react";
import { Loader2, Save, CheckCircle2, WifiOff } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Stars } from "@/components/recording/Stars";
import { RecordingPlayer } from "@/components/recording/RecordingPlayer";
import { fmt } from "@/components/recording/useRecorder";

export const RecordingReview = ({ r, onSave }) => {
  const { t } = useLang();
  const tr = t.rec;
  const [rating, setRating] = useState(r.rating || 0);
  const [feedback, setFeedback] = useState(r.feedback || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave(r, { rating, feedback });
    setSaving(false);
  };

  return (
    <article className="bg-white border border-[#E5DCCB] p-5 flex flex-col gap-3" data-testid={`teacher-recording-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold" data-testid={`teacher-recording-student-${r.id}`}>{r.student_name}</p>
          <p className="text-xs text-stone-600 mt-1" dir="auto">{r.book?.title || t.teacher.deletedBook} · {tr.page(r.page + 1)} · {fmt(r.duration)}</p>
          <p className="text-xs text-stone-600">{new Date(r.recorded_at || r.created_at).toLocaleString()}</p>
          {r.offline && (
            <span className="mt-1 inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 bg-sky-100 text-sky-900" data-testid={`teacher-recording-offline-${r.id}`}>
              <WifiOff className="w-3.5 h-3.5" /> {tr.tOffline}
            </span>
          )}
        </div>
        {r.reviewed_at ? (
          <span className="shrink-0 inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 bg-emerald-100 text-emerald-900"><CheckCircle2 className="w-3.5 h-3.5" /> {tr.tReviewed}</span>
        ) : <span className="shrink-0 text-xs rounded-full px-2.5 py-1 bg-amber-100 text-amber-900">{tr.tPending}</span>}
      </div>
      <RecordingPlayer id={r.id} />
      <Stars value={rating} onChange={setRating} size="w-6 h-6" testId={`teacher-recording-stars-${r.id}`} />
      <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} maxLength={500} rows={2} dir="auto"
        placeholder={tr.tFeedback} data-testid={`teacher-recording-feedback-${r.id}`}
        className="w-full border border-[#E5DCCB] rounded-sm px-3 py-2 text-sm bg-[#FAF7F2] focus:outline-none focus:border-[#047857]" />
      <button onClick={save} disabled={saving} data-testid={`teacher-recording-save-${r.id}`}
        className="self-start inline-flex items-center gap-2 rounded-full bg-[#047857] text-white px-5 py-2 text-sm font-semibold hover:bg-[#065F46] transition-colors disabled:opacity-50">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {tr.tSave}
      </button>
    </article>
  );
};
